import { findProvinceForCity } from './chinaCities.js';

export const NEARBY_RADIUS_KM = 80;
export const UNKNOWN_LOCATION = '未知位置';

const stripAdminSuffix = (name) =>
  String(name || '')
    .trim()
    .replace(/(特别行政区|自治区|自治州|地区|盟|省|市|区|县)$/g, '')
    .trim();

export const normalizeCityName = (name) => stripAdminSuffix(name);

export const normalizeProvinceName = (name) => stripAdminSuffix(name);

export const formatLocationLabel = (city, province) => {
  const c = normalizeCityName(city);
  const p = normalizeProvinceName(province);
  if (!c || c === UNKNOWN_LOCATION) return UNKNOWN_LOCATION;
  if (!p || p === c) return c;
  return `${p}·${c}`;
};

export const haversineKm = (lat1, lon1, lat2, lon2) => {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

export const getDevicePosition = (options = {}) =>
  new Promise((resolve, reject) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      reject(new Error('浏览器不支持定位'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) =>
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        }),
      (error) => reject(error),
      {
        enableHighAccuracy: false,
        timeout: 5000,
        maximumAge: 300000,
        ...options
      }
    );
  });

/** 先快速定位（网络/IP），失败再高精度 GPS */
export const getDevicePositionSmart = async () => {
  try {
    return await getDevicePosition({
      enableHighAccuracy: false,
      timeout: 4000,
      maximumAge: 600000
    });
  } catch {
    return getDevicePosition({
      enableHighAccuracy: true,
      timeout: 8000,
      maximumAge: 0
    });
  }
};

const GEO_REVERSE_CACHE_KEY = 'yugui_reverse_geo_cache';

const readReverseGeoCache = (latitude, longitude) => {
  try {
    const key = `${latitude.toFixed(2)},${longitude.toFixed(2)}`;
    const raw = sessionStorage.getItem(GEO_REVERSE_CACHE_KEY);
    const map = raw ? JSON.parse(raw) : {};
    return map[key] || null;
  } catch {
    return null;
  }
};

const writeReverseGeoCache = (latitude, longitude, value) => {
  try {
    const key = `${latitude.toFixed(2)},${longitude.toFixed(2)}`;
    const raw = sessionStorage.getItem(GEO_REVERSE_CACHE_KEY);
    const map = raw ? JSON.parse(raw) : {};
    map[key] = value;
    const entries = Object.entries(map).slice(-20);
    sessionStorage.setItem(GEO_REVERSE_CACHE_KEY, JSON.stringify(Object.fromEntries(entries)));
  } catch {
    /* ignore */
  }
};

const fetchReverseGeo = async (url) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 4500);
  try {
    const res = await fetch(url, { signal: controller.signal });
    if (!res.ok) return null;
    return res.json();
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
};

const parseReverseGeocodePayload = (data, coords, source) => {
  const city =
    normalizeCityName(
      data?.city ||
        data?.locality ||
        data?.town ||
        data?.county ||
        data?.address?.city ||
        data?.address?.town ||
        data?.address?.county
    ) || '';
  const province =
    normalizeProvinceName(
      data?.principalSubdivision ||
        data?.state ||
        data?.address?.state ||
        data?.address?.province
    ) || findProvinceForCity(city);

  if (!city) return null;

  return {
    city,
    province: province || findProvinceForCity(city),
    locationData: coords,
    locationSource: source,
    locationAgreed: source === 'gps'
  };
};

export const reverseGeocode = async (latitude, longitude) => {
  const coords = { latitude, longitude };
  const cached = readReverseGeoCache(latitude, longitude);
  if (cached) return cached;

  const bigData = await fetchReverseGeo(
    `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=zh`
  );
  if (bigData) {
    const parsed = parseReverseGeocodePayload(bigData, coords, 'gps');
    if (parsed) {
      writeReverseGeoCache(latitude, longitude, parsed);
      return parsed;
    }
  }

  const osm = await fetchReverseGeo(
    `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&accept-language=zh-CN&zoom=10`
  );
  if (osm) {
    const parsed = parseReverseGeocodePayload(osm, coords, 'gps');
    if (parsed) {
      writeReverseGeoCache(latitude, longitude, parsed);
      return parsed;
    }
  }

  return null;
};

export const detectUserLocation = async () => {
  const coords = await getDevicePositionSmart();
  const resolved = await reverseGeocode(coords.latitude, coords.longitude);
  if (resolved) return resolved;
  throw new Error('无法识别所在城市，请手动选择');
};

export const buildManualLocation = (province, city) => {
  const normalizedCity = normalizeCityName(city);
  const normalizedProvince = normalizeProvinceName(province) || findProvinceForCity(normalizedCity);
  if (!normalizedCity) return null;
  return {
    city: normalizedCity,
    province: normalizedProvince,
    locationData: null,
    locationSource: 'manual',
    locationAgreed: false
  };
};

export const buildLocationProfile = (info) => {
  if (!info?.city) {
    return {
      location: UNKNOWN_LOCATION,
      city: '',
      province: '',
      locationData: null,
      locationSource: '',
      locationAgreed: false
    };
  }
  return {
    location: formatLocationLabel(info.city, info.province),
    city: normalizeCityName(info.city),
    province: normalizeProvinceName(info.province) || findProvinceForCity(info.city),
    locationData: info.locationData || null,
    locationSource: info.locationSource || 'manual',
    locationAgreed: !!info.locationAgreed
  };
};

export const resolveProfileLocationFields = (locationInfo, locationAgreed, serverUser = {}, cachedProfile = {}) => {
  if (locationInfo?.city) {
    return buildLocationProfile({ ...locationInfo, locationAgreed: locationAgreed ?? locationInfo.locationAgreed });
  }
  if (cachedProfile?.city) {
    return buildLocationProfile(cachedProfile);
  }
  if (serverUser?.city) {
    return buildLocationProfile(serverUser);
  }
  const legacyLocation = serverUser?.location || cachedProfile?.location;
  if (legacyLocation && legacyLocation !== UNKNOWN_LOCATION && legacyLocation !== '同城') {
    const city = normalizeCityName(legacyLocation.split('·').pop());
    return buildLocationProfile({
      city,
      province: findProvinceForCity(city),
      locationData: serverUser?.locationData || cachedProfile?.locationData || null,
      locationSource: cachedProfile?.locationSource || 'manual',
      locationAgreed: !!(serverUser?.locationData || cachedProfile?.locationData)
    });
  }
  return buildLocationProfile(null);
};

export const getPetLocationSnapshot = (pet, publisherProfile) => {
  const city =
    normalizeCityName(pet?.city) ||
    normalizeCityName(pet?.userLocation) ||
    normalizeCityName(pet?.location?.split?.('·')?.pop?.()) ||
    normalizeCityName(publisherProfile?.city) ||
    '';
  const province =
    normalizeProvinceName(pet?.province) ||
    normalizeProvinceName(publisherProfile?.province) ||
    findProvinceForCity(city);
  const locationData = pet?.locationData || publisherProfile?.locationData || null;
  return {
    city,
    province,
    locationData,
    label: city ? formatLocationLabel(city, province) : UNKNOWN_LOCATION
  };
};

/** 同城=3，同省=2，邻近(<=80km)=1，不匹配=0 */
export const getLocationMatchScore = (viewerProfile, pet, publisherProfile) => {
  if (!viewerProfile?.city || viewerProfile.city === UNKNOWN_LOCATION) return 0;

  const viewerCity = normalizeCityName(viewerProfile.city);
  const viewerProvince = normalizeProvinceName(viewerProfile.province) || findProvinceForCity(viewerCity);
  const petLoc = getPetLocationSnapshot(pet, publisherProfile);

  if (!petLoc.city) return 0;
  if (petLoc.city === viewerCity) return 3;
  if (petLoc.province && viewerProvince && petLoc.province === viewerProvince) return 2;

  const a = viewerProfile.locationData;
  const b = petLoc.locationData;
  if (a?.latitude != null && b?.latitude != null) {
    const km = haversineKm(a.latitude, a.longitude, b.latitude, b.longitude);
    if (km <= NEARBY_RADIUS_KM) return 1;
  }
  return 0;
};

export const isNearbyAdoptMatch = (viewerProfile, pet, publisherProfile) =>
  getLocationMatchScore(viewerProfile, pet, publisherProfile) > 0;

export const sortAdoptsByLocation = (list, viewerProfile, publisherLookup) => {
  if (!viewerProfile?.city) return list;
  return [...list].sort((a, b) => {
    const scoreA = getLocationMatchScore(viewerProfile, a, publisherLookup?.(a));
    const scoreB = getLocationMatchScore(viewerProfile, b, publisherLookup?.(b));
    if (scoreB !== scoreA) return scoreB - scoreA;
    return (b.createdAt || 0) - (a.createdAt || 0);
  });
};

export const getGeolocationErrorMessage = (error) => {
  const code = error?.code ?? error?.PERMISSION_DENIED;
  switch (code) {
    case 1:
    case error?.PERMISSION_DENIED:
      return '您拒绝了位置授权，可手动选择城市';
    case 2:
    case error?.POSITION_UNAVAILABLE:
      return '定位服务不可用，请手动选择城市';
    case 3:
    case error?.TIMEOUT:
      return '定位超时，请手动选择城市';
    default:
      return error?.message || '定位失败，请手动选择城市';
  }
};

export const hasValidProfileCity = (profile) =>
  !!(profile?.city && profile.city !== UNKNOWN_LOCATION);

/** 合并资料时优先保留有真实城市的来源，避免云库旧「未知位置」覆盖新定位 */
export const pickBestLocationFields = (primary = {}, fallback = {}) => {
  const primaryOk = hasValidProfileCity(primary);
  const fallbackOk = hasValidProfileCity(fallback);
  const source = primaryOk ? primary : fallbackOk ? fallback : primary;
  const other = source === primary ? fallback : primary;

  if (hasValidProfileCity(source)) {
    return {
      location: source.location || formatLocationLabel(source.city, source.province),
      city: source.city,
      province: source.province || findProvinceForCity(source.city),
      locationData: source.locationData ?? other.locationData ?? null,
      locationSource: source.locationSource || other.locationSource || '',
      locationAgreed: source.locationAgreed ?? other.locationAgreed ?? false
    };
  }

  return {
    location: UNKNOWN_LOCATION,
    city: '',
    province: '',
    locationData: null,
    locationSource: '',
    locationAgreed: false
  };
};

export const applyAuthLocationToProfile = (profile, locationInfo, locationAgreed) => {
  if (!locationInfo?.city && locationAgreed == null && locationInfo?.locationAgreed == null) {
    return profile;
  }
  if (!locationInfo?.city && (locationAgreed != null || locationInfo?.locationAgreed != null)) {
    return {
      ...profile,
      locationAgreed: !!(locationAgreed ?? locationInfo?.locationAgreed)
    };
  }
  if (!locationInfo?.city) return profile;
  return {
    ...profile,
    ...buildLocationProfile({
      ...locationInfo,
      locationAgreed: locationAgreed ?? locationInfo.locationAgreed
    })
  };
};

export const setAutoLocationConsent = (profile, agreed) => {
  if (!profile) return profile;
  return {
    ...profile,
    locationAgreed: !!agreed,
    locationSource:
      agreed || !profile.city
        ? profile.locationSource
        : profile.locationSource === 'gps'
          ? 'manual'
          : profile.locationSource
  };
};

export const formatProfileLocationDisplay = (profile) => {
  if (hasValidProfileCity(profile)) {
    return profile.location || formatLocationLabel(profile.city, profile.province);
  }
  if (
    profile?.location &&
    profile.location !== UNKNOWN_LOCATION &&
    profile.location !== '同城'
  ) {
    return profile.location;
  }
  return UNKNOWN_LOCATION;
};
